<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      :active-step="pageTitle"
    />
    <PageHeading :default-heading="pageTitle" />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          Does your trust meet these conditions?
        </h2>
        <p>To continue online:</p>
        <ul class="pvd-list-bullet">
          <li>
            The
            <a
              id="trustees-grantors-modal-trigger"
              href="javascript:void(0);"
            >
              trustees and grantors</a>
            must have a valid U.S. SSN.
            <pvd-modal
              id="trustees-grantors-modal"
              for="trustees-grantors-modal-trigger"
              class="small"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  Trust roles
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    <strong>Trustee</strong><br>
                    The trustee is a person or institution who has control of a
                    trust’s assets and has a fiduciary responsibility to
                    appropriately handle the assets. A Fidelity trust account
                    needs at least one trustee, with a maximum number of 4.
                  </p>
                  <p>
                    <strong>Grantor</strong><br>
                    A grantor is the person who creates or contributes the
                    assets to a trust. A Fidelity trust account needs at least
                    one grantor, with a maximum of 6.
                  </p>
                </div>
              </div>
            </pvd-modal>
          </li>
          <li>
            The trustees must be people, and not corporations or other
            <a
              id="entities-modal-trigger"
              href="javascript:void(0);"
            >entities</a>.
            <pvd-modal
              id="entities-modal"
              for="entities-modal-trigger"
              class="small"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  Entity
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    An entity as Trustee refers to a business such as a
                    corporation, bank, law firm or other professional trustee
                    company.
                  </p>
                </div>
              </div>
            </pvd-modal>
          </li>
        </ul>
        <div>
          <ToggleComponent
            id-prefix="trust-conditions-agreement"
            :toggle-value="meetsConditions"
            yes-label="Yes"
            no-label="No"
            label="Continue online?"
            :track-events="true"
            :validation-rules="selectValidation"
            message="Please make a selection."
            @update:toggleValue="meetsConditionsChanged"
          />
          <div
            v-if="meetsConditions === 'No'"
            class="pvd-display-row mb-2"
          >
            <div class="display-row-supplemental">
              <pvd-system-message
                placement="inline"
                type="info"
                :message="getToggletext"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-show="showAdditionalInformation">
        <div class="pvd-container container-divider">
          <h2 class="pvd-heading-L">
            Now, we need a few more details about the trust
          </h2>
          <p>
            To fill in the fields below, please reference your trust agreement.
          </p>
          <apx-text-input
            id="trustName"
            :value="trustName"
            label="Trust name"
            name="trustName"
            type="text"
            class="pvd-input"
            icon-left="true"
            help-header="Trust name"
            help-body="The trust name is the full legal name of the trust listed in your original trust agreement document."
            help-size="small"
            input-type="text"
            :validation-rules="trustNameValidationRules"
            :validation-text="trustNameValidationText"
            @change="trustNameChanged"
          />
          <div class="pvd-form-row">
            <div class="form-row-label">
              &nbsp;
            </div>
            <div
              v-if="routeId !== 'managed'"
              class="form-row-content"
            >
              <div class="pvd-checkbox">
                <input
                  id="trust-type--irrevocable"
                  v-model="isIrrevocableValue"
                  type="checkbox"
                  name="trust-type"
                  value="irrevocable"
                >
                <label for="trust-type--irrevocable">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" /> </span>This trust is irrevocable
                  <a
                    id="irrevocable-trust-modal-trigger"
                    class="ml-1"
                    href="javascript:void(0);"
                  >
                    <pvd-icon
                      icon="help"
                      class="color-link"
                    />
                  </a>
                </label>
              </div>
            </div>
          </div>
          <pvd-modal
            id="irrevocable-trust-modal"
            for="irrevocable-trust-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Irrevocable trust
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  An irrevocable trust can't be modified, amended or terminated
                  without the permission of the beneficiary or beneficiaries. If
                  your trust is irrevocable, please select this option.
                </p>
              </div>
            </div>
          </pvd-modal>
          <div class="pvd-form-row ssn-tin__toggle">
            <div class="form-row-label label-icon">
              <label for="ssn-tin-group-label">
                <a
                  id="ssn-tin-modal-trigger"
                  href="javascript:void(0);"
                >
                  <pvd-icon
                    data-v-6e60c119=""
                    icon="help"
                    class="m-small-left"
                  />
                </a>
                Taxpayer ID / Grantor SSN
              </label>
            </div>
            <div class="form-row-content">
              <div
                class="pvd-toggle-btn"
                aria-describedby="ssn-tin-toggle-error-message"
                role="radiogroup"
                aria-labelledby="ssn-tin-group-label"
              >
                <input
                  id="ssn-tin-first-toggle"
                  v-model="idType"
                  type="radio"
                  value="tin"
                  name="ssn-tin-toggle"
                  aria-describedby="ssn-tin-toggle-error-message"
                  @change="idTypeChanged"
                >
                <label for="ssn-tin-first-toggle">
                  <pvd-icon
                    icon="checkmark"
                  />
                  TIN
                </label>
                <input
                  id="ssn-tin-second-toggle"
                  v-model="idType"
                  type="radio"
                  value="ssn"
                  name="ssn-tin-toggle"
                  aria-describedby="ssn-tin-toggle-error-message"
                  @change="idTypeChanged"
                >
                <label for="ssn-tin-second-toggle">
                  <pvd-icon
                    icon="checkmark"
                  />
                  Grantor SSN
                </label>
              </div>
              <div
                class="form-row-message"
                style="display: none;"
              >
                <pvd-system-message
                  id="ssn-tin-toggle-error-message"
                  placement="inline"
                  type="alert"
                  message="An option must be selected."
                />
              </div>
            </div>
            <pvd-modal
              id="ssn-tin-modal"
              for="ssn-tin-modal-trigger"
              class="small"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  Taxpayer ID / Grantor SSN
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    The number the trust will use for IRS reporting (listed in
                    the original trust agreement). If using an SSN, it must
                    match the SSN of a living grantor listed on the account.
                  </p>
                </div>
              </div>
            </pvd-modal>
          </div>
          <apx-ssn
            v-if="showSSNInput"
            id="ssn"
            :value="trustSSN"
            class="ssn"
            label="Grantor Social Security number"
            supplemental-text="Please ensure the SSN provided matches the SSN of a living grantor on the trust."
            :required="showSSNInput"
            :validation-rules="buildTrustSSNRules()"
            @value-changed="ssnChanged($event)"
          />
          <div v-if="!showSSNInput">
            <apx-text-input
              id="taxpayerID"
              :value="taxpayerID"
              label="Trust taxpayer ID number"
              name="taxpayerID"
              type="number"
              class="pvd-input"
              :validation-rules="buildTrustTINRules()"
              :validation-text="taxpayerIDValidationText"
              @change="taxpayerIDChanged"
            />
          </div>
          <div class="pvd-form-row ssn-tin__toggle" v-if="isTrustAccount">
            <div class="form-row-label label-icon">
              <label for="backupWithHolding-group-label">
                <a
                  id="backupWithHolding-modal-trigger"
                  href="javascript:void(0);"
                >
                  <pvd-icon
                    data-v-6e60c119=""
                    icon="help"
                    class="m-small-left"
                  />
                </a>
                IRS backup withholding
              </label>
            </div>
            <div class="form-row-content">
              <div
                class="pvd-toggle-btn"
                aria-describedby="backupWithHolding-toggle-error-message"
                role="radiogroup"
                aria-labelledby="backupWithHolding-group-label"
              >
                <input
                  id="backupWithHolding-first-toggle"
                  v-model="backupWithHolding"
                  type="radio"
                  value="Yes"
                  name="backupWithHolding-toggle"
                  aria-describedby="backupWithHolding-toggle-error-message"
                  @change="backupWithHoldingChanged"
                >
                <label for="backupWithHolding-first-toggle">
                  <pvd-icon
                    icon="checkmark"
                  />
                  Yes
                </label>
                <input
                  id="backupWithHolding-second-toggle"
                  v-model="backupWithHolding"
                  type="radio"
                  value="No"
                  name="backupWithHolding-toggle"
                  aria-describedby="backupWithHolding-toggle-error-message"
                  @change="backupWithHoldingChanged"
                >
                <label for="backupWithHolding-second-toggle">
                  <pvd-icon
                    icon="checkmark"
                  />
                  No
                </label>
              </div>
              <div
                class="form-row-message"
                style="display: none;"
              >
                <pvd-system-message
                  id="backupWithHolding-toggle-error-message"
                  placement="inline"
                  type="alert"
                  message="An option must be selected."
                />
              </div>
            </div>
            <pvd-modal
              id="backupWithHolding-modal"
              for="backupWithHolding-modal-trigger"
              class="small"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  IRS mandated backup withholding
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    You must indicate Yes if you have been notified by the IRS that the
                     trust taxpayer ID is currently subject to backup withholding because
                     of a failure to report all interest and dividends on a tax return.
                  </p>
                </div>
              </div>
            </pvd-modal>
          </div>
          <div class="pvd-form-row ssn-tin__toggle" style="margin-top: 0px;" v-if="isTrustAccount">
            <div class="form-row-label"/>
            <div class="form-row-content">
              <div class="form-row-supplemental" v-if="showBackupWithHoldingHelpingText">
                  <p class="xsmall">
                    This is not common. By selecting Yes, you are stating that the trust taxpayer ID
                    has been notified by the Internal Revenue Service (IRS) that it is subject to
                    backup withholding as a result of failure to report all interest and dividends.
                  </p>
              </div>
            </div>
          </div>
          <apx-date-select
            id="dateOfTrust"
            group-label="Date of trust"
            :day-value="dotDay"
            :month-value="dotMonth"
            :year-value="dotYear"
            validation-text="The date entered is not a valid date."
            default-select-label="Select"
            :min-date="pd_content.min_date_allowed_for_dob"
            required
            :date-input-value="dateOfTrust"
            icon-left="true"
            help-header="Date of trust"
            help-body="The date the trust was created is the “date of trust” and is listed in the original trust agreement document."
            help-size="small"
            @value-changed="dateOfTrustChanged"
          />
          <SelectStateComponent
            :trust-state.sync="trustState"
            :id-prefix="'trustState'"
            name="trustState"
            @update:trustState="trustStateChanged"
          />
          <div
            :key="renderKey"
          >
            <div
              v-for="(beneficiary, index) in beneficiaries"
              :key="index"
            >
              <apx-full-name
                :id="!isManagedAccount ? `benefitOf-${index}` : 'benefitOf'"
                :group-label="
                  !isManagedAccount ? 'For the Benefit of' : 'Beneficiary name'
                "
                :first-name.sync="beneficiary.firstName"
                first-name-label="First"
                :middle-name.sync="beneficiary.middleName"
                middle-name-label="Middle"
                :last-name.sync="beneficiary.lastName"
                last-name-label="Last"
                :icon-left="addBeneHelpModal(index) ? true : undefined"
                :help-header="
                  addBeneHelpModal(index) ? 'Beneficiary' : undefined
                "
                :help-body="
                  addBeneHelpModal(index)
                    ? 'The beneficiary is the person, or people, who will receive the trust assets for their benefit.'
                    : undefined
                "
                :help-size="addBeneHelpModal(index) ? 'small' : undefined"
                :validation-rules="
                  !isManagedAccount ? beneficiaryValidationRules : ''
                "
                @value-changed="benefitOfChanged(index, $event)"
              />
              <button
                v-show="moreThanOneBene()"
                id="removeBene"
                class="pvd-btn btn-anchor remove-bene"
                type="button"
                @click="removeBene(index)"
              >
                <pvd-icon
                  icon="delete"
                  class="color-action remove"
                />
                Remove
              </button>
            </div>
          </div>

          <div
            class="pvd-form-row"
          >
            <div class="form-row-label form-row-label-top">
&nbsp;
            </div>
            <div class="form-row-content">
              <button
                v-if="belowMaxBenes"
                id="addAnotherBene"
                class="pvd-btn btn-ghost-action add-another"
                type="button"
                @click="addAnotherBene"
              >
                Add another person
              </button>
            </div>
          </div>
        </div>

        <div
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            What is the legal address of the trust?
          </h2>
          <p>
            This is the address that will be used on all IRS filings. It can’t
            be a post office box, or a storefront (such as a UPS store) where
            mail can be received. If no legal address is listed in your trust
            agreement, you can use your legal address.
          </p>
          <apx-address
            :address-line1="trustAddress.line1"
            :address-line2="trustAddress.line2"
            :city-name="trustAddress.city"
            :state-code="trustAddress.state"
            :zip-code="trustAddress.zip"
            id-prefix="trust-legal-address"
            prevent-pobox
            required
            @value-changed="trustAddressChanged($event)"
          />
        </div>
      </div>
      <PageNavigation
        primary-button-type="button"
        :show-primary-button-spinner="shouldShowLoadingSpinner"
        :disable-primary-button="shouldDisableNext"
        :primary-button-label="primaryButtonLabel"
        :secondary-button-label="secondaryButtonLabel"
        :page-exit-url="pd_content.overlay_exit_link.overview_url"
        @primary-button-clicked="nextButtonClicked"
      />
    </form>
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>
<script>
import ProgressBar from '../components/ProgressBar.vue';
import PageNavigation from '../components/PageNavigation.vue';
import PageHeading from '../components/PageHeading.vue';
import addressAutocomplete from '../mixins/addressAutocomplete';
import dataHelper from '../mixins/dataHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';
import ToggleComponent from '../components/ToggleComponent.vue';
import trustHelper from '../mixins/trustHelper';
import SelectStateComponent from '../components/SelectStateComponent.vue';

export default {
  name: 'TrustInformation',
  components: {
    ProgressBar,
    PageNavigation,
    PageHeading,
    ToggleComponent,
    SelectStateComponent,
  },
  mixins: [
    addressAutocomplete,
    dataHelper,
    dataSanitize,
    measurement,
    utilityFunctions,
    validationHelper,
    widgetHelper,
    pageNavigationHelper,
    trustHelper,
  ],
  data() {
    return {
      backButtonAttempt: false,
      selectValidation: {
        regex: '^(Yes|No)$',
        required: 'true',
      },
      meetsConditions: '',
      trustState: '',
      trustName: '',
      backupWithHolding: 'No',
      idType: 'tin',
      trustSSN: '',
      taxpayerID: '',
      dateOfTrust: '',
      trustAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      beneficiaries: [
        {
          firstName: '',
          middleName: '',
          lastName: '',
        },
      ],
      additionalRoles: [],
      applicant: {},
      maxBenes: 3,
      legalAddressSelection: 'existing',
      trustNameValidationRules:
        '{"validations":{"required":true,"max":50,"min":1,"regex":"^[-;,./?0-9a-zA-Z `~!@#$^&*_=+{}|\']+$"}}',
      trustNameValidationText:
        '{"validations":{"required":"Please enter a valid trust name.","max":"Please enter a valid trust name.","min":"Please enter a valid trust name.","regex":"Please enter a valid trust name."}}',
      taxpayerIDValidationRules:
        '{"validations":{"required":true,"max":9,"min":9,"regex":"^[0-9]{9}$"}}',
      taxpayerIDValidationText:
        '{"validations":{"required":"Please enter a valid taxpayer ID number.","max":"Please enter a valid taxpayer ID number.","min":"Please enter a valid taxpayer ID number.","regex":"Please enter a valid taxpayer ID number."}}',
      beneficiaryValidationRules: JSON.stringify({
        firstName: {
          required: false,
          max: 25,
          min: 0,
          regex: "^[a-zA-Z0-9 '.,-]*$",
        },
        lastName: {
          required: false,
          max: 30,
          min: 0,
          regex: "^[a-zA-Z0-9 '.,-]*$",
        },
        middleName: {
          required: false,
          max: 10,
          min: 0,
          regex: "^[a-zA-Z0-9 '.,-]*$",
        },
      }),
      beneficiaryValidationText: JSON.stringify({
        firstName: {
          max: 'First name has too many characters.',
          min: 'First name must be more than an initial.',
          regex: 'First name contains invalid characters.',
        },
        lastName: {
          max: 'Last name has too many characters.',
          min: 'Last name must be more than an initial.',
          regex: 'Last name contains invalid characters.',
        },
        middleName: {
          max: 'Middle name has too many characters.',
          regex: 'Middle name contains invalid characters.',
        },
      }),
      renderKey: 0,
      isLoading: false,
      pageTitle: 'Tell us about your trust',
      isIrrevocable: false,
      editMode: false,
    };
  },
  computed: {
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },
    isTrustAccount() {
      return this.isTrust(this.routeId);
    },
    showAdditionalInformation() {
      return this.meetsConditions === 'Yes';
    },
    showBackupWithHoldingHelpingText() {
      return this.backupWithHolding === 'Yes';
    },
    showSSNInput() {
      return this.idType === 'ssn';
    },
    dotMonth() {
      return this.dateOfTrust ? this.dateOfTrust.split('/')[0] : '';
    },
    dotDay() {
      return this.dateOfTrust ? this.dateOfTrust.split('/')[1] : '';
    },
    dotYear() {
      return this.dateOfTrust ? this.dateOfTrust.split('/')[2] : '';
    },
    belowMaxBenes() {
      return this.beneficiaries.length < this.maxBenes;
    },
    shouldDisableNext() {
      if (
        this.isLoading
        || (this.meetsConditions === 'No' && this.routeId === 'managed')
      ) {
        return 'Y';
      }
      return 'N';
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    primaryButtonLabel() {
      if (this.isLoading) return this.pd_content.primary_button.wait;
      return this.editMode ? this.pd_content.primary_button.edit : this.pd_content.primary_button.default;
    },
    secondaryButtonLabel() {
      return this.editMode ? this.pd_content.secondary_button.edit : this.pd_content.secondary_button.default;
    },
    getToggletext: {
      get() {
        if (this.routeId === 'managed') {
          if (
            this.pd_pageData.tStoreResponse
            && this.pd_pageData.tStoreResponse.params
            && this.pd_pageData.tStoreResponse.params.accountNumber
          ) {
            return this.pd_content.managed_trust_information_toggle
              .managed_toggle_text;
          }
          return this.pd_content.managed_trust_information_toggle
            .managed_toggle_text_macc;
        }
        return this.pd_content.managed_trust_information_toggle
          .nonmanaged_toggle_text;
      },
    },
    isIrrevocableValue: {
      get() {
        return this.isIrrevocable;
      },
      set(newIsIrrevocable) {
        this.isIrrevocable = newIsIrrevocable;
        this.commitAccountData(this.isIrrevocable, 'isIrrevocable');
      },
    },
    trustLegalAddressSelectionValue: {
      get() {
        if (
          this.acc_details.trustAddress
          && this.acc_details.trustAddress.state
        ) {
          return 'new';
        }
        return this.legalAddressSelection;
      },
      set(newLegalAddressSelection) {
        this.legalAddressSelection = newLegalAddressSelection;
        this.commitAccountData(
          this.legalAddressSelection,
          'trustLegalAddressSelection',
        );
      },
    },
  },
  created() {
    if (this.cs_editMode) {
      this.unsubscribe = this.sanitizeData('primaryUser');
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  mounted() {
    this.track(this.cs_stateName, 'pageview');
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.removeModalClassIfExists();
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));

    document.title = this.setTitle(this.pageTitle);

    this.applicant = this.getInstanceData().formData.primaryUser;
    this.additionalRoles = this.getInstanceData().formData.additionalUsers;
    this.editMode = this.getInstanceData().currentState?.editMode;
    this.isLoading = false;

    const changeEventName = 'value-changed';

    const dateOfTrustComponent = document.querySelector('#dateOfTrust');
    dateOfTrustComponent.addEventListener(
      changeEventName,
      this.dateOfTrustChanged,
    );

    this.initGoogleAutocomplete(
      'trust-legal-address-addressLine1',
      this.trustFillAddressValues.bind(
        null,
        'trust-legal-address',
        this.trustAddressChanged.bind(this),
      ),
    );

    // Repopulate the data if it exists.
    if (this.acc_details.meetsConditions) {
      this.meetsConditions = this.acc_details.meetsConditions;
    }
    if (this.acc_details.trustName) {
      this.trustName = this.acc_details.trustName;
    }
    if (this.acc_details.dateOfTrust) {
      this.dateOfTrust = this.acc_details.dateOfTrust;
    }
    if (this.acc_details.trustAddress) {
      this.trustAddress = this.acc_details.trustAddress;
    }
    if (this.acc_details.trustLegalAddressSelection) {
      this.legalAddressSelection = this.acc_details.trustLegalAddressSelection;
    }
    if (this.acc_details.trustState) {
      this.trustState = this.acc_details.trustState;
    }
    if (
      this.acc_details.beneficiaries
      && this.acc_details.beneficiaries.length > 0
    ) {
      this.beneficiaries = this.acc_details.beneficiaries;
    }
    if (
      this.pd_pageData.tStoreResponse
      && this.pd_pageData.tStoreResponse.params
      && this.irrevocableTrustRegType(
        this.pd_pageData.tStoreResponse.params.regType,
      )
    ) {
      this.acc_details.isIrrevocable = true;
    }
    if (typeof this.acc_details.isIrrevocable !== 'undefined') {
      this.isIrrevocable = this.acc_details.isIrrevocable;
    } else {
      this.commitAccountData(false, 'isIrrevocable');
    }

    if (this.acc_details.taxIDType) {
      this.idType = this.acc_details.taxIDType;
      this.trustSSN = this.acc_details.ssn ? this.acc_details.ssn : '';
      this.taxpayerID = this.acc_details.tin ? this.acc_details.tin : '';
    } else {
      this.commitAccountData('tin', 'taxIDType');
      this.commitAccountData(undefined, 'tin');
      this.commitAccountData(undefined, 'ssn');
    }
    if (this.acc_details?.backupWithHolding) {
      this.controlBackupWithHoldingChanged(this.acc_details.backupWithHolding);
    }
  },

  methods: {
    nextButtonClicked(navAction) {
      this.isLoading = true;
      if (this.meetsConditions === 'No') {
        this.$store.commit('currentState/updateField', {
          path: 'destinationState',
          value: 'trust-paper',
        });
      } else {
        this.$store.commit('currentState/updateField', {
          path: 'destinationState',
          value: this.editMode ? 'trust-review-and-confirm' : 'trust-role',
        });
      }
      this.performPageValidation(navAction).then((valid) => {
        if (valid || this.meetsConditions === 'No') {
          if (this.isIrrevocable) {
            // Remove revoke access from the primary user who might have been given it (edit scenario)
            this.commitPrimaryUserData(false, 'additionalDetails.hasRevoke');
            if (
              this.pu_additionalDetails
              && !this.pu_additionalDetails.hasAmend
            ) {
              // in this case we need to set hasAmendRevoke to false because hasRevoke is now false and hasAmend was already false
              this.commitPrimaryUserData(
                false,
                'additionalDetails.hasAmendRevoke',
              );
            }

            if (this.au_details && Array.isArray(this.au_details)) {
              // Delete the user if they only have authorities and none of those authorities exist anymore
              const remainingAdditionalUsers = this.au_details
                .filter(
                  (user) => user.role !== 'authority'
                    || user.hasAmend
                    || user.hasAppointRemove,
                )
                .map((user) => ({
                  ...user,
                  hasRevoke: false,
                  hasAmendRevoke: user.hasAmend,
                }));

              this.setListInstanceData(
                'formData',
                remainingAdditionalUsers,
                'additionalUsers',
              );
            }
          }
          this.performNavigation(this.pd_content.primary_button.value);
        } else {
          this.isLoading = false;
        }
      });
    },
    irrevocableTrustRegType(regtype) {
      return this.isIrrevocableRegType(regtype);
    },
    addAnotherBene() {
      this.beneficiaries = [
        ...this.beneficiaries,
        {
          firstName: '',
          middleName: '',
          lastName: '',
        },
      ];
    },
    controlBackupWithHoldingChanged(toggleValue) {
      this.commitAccountData(toggleValue, 'backupWithHolding');
      this.backupWithHolding = toggleValue;
    },
    removeBene(index) {
      this.beneficiaries = this.beneficiaries.filter((bene, i) => index !== i);
      this.renderKey += 1;
    },
    meetsConditionsChanged(toggleValue) {
      this.meetsConditions = toggleValue;
      this.commitAccountData(this.meetsConditions, 'meetsConditions');
    },
    trustNameChanged(event) {
      this.trustName = event.target.value;
      this.commitAccountData(this.trustName, 'trustName');
    },
    taxpayerIDChanged(event) {
      this.taxpayerID = event.target.value;
      this.commitAccountData(this.taxpayerID, 'tin');
    },
    idTypeChanged(event) {
      if (event?.target?.value) {
        this.idType = event?.target?.value;
        const tin = this.idType.toUpperCase() === 'TIN' ? this.taxpayerID : undefined;
        const ssn = this.idType.toUpperCase() === 'SSN' ? this.trustSSN : undefined;
        this.commitAccountData(this.idType, 'taxIDType');
        this.commitAccountData(tin, 'tin');
        this.commitAccountData(ssn, 'ssn');
      }
    },
    backupWithHoldingChanged(event) {
      if (event?.target?.value) {
        this.backupWithHolding = event?.target?.value;
        this.commitAccountData(this.backupWithHolding, 'backupWithHolding');
      }
    },
    ssnChanged(event) {
      this.trustSSN = event.detail.value;
      this.commitAccountData(this.trustSSN, 'ssn');
    },
    dateOfTrustChanged(event) {
      this.dateOfTrust = `${event.detail.month}/${event.detail.day}/${event.detail.year}`;
      this.commitAccountData(
        `${event.detail.month}/${event.detail.day}/${event.detail.year}`,
        'dateOfTrust',
      );
    },
    trustStateChanged(value) {
      this.trustState = value;
      this.commitAccountData(this.trustState, 'trustState');
    },
    benefitOfChanged(index, event) {
      this.beneficiaries[index] = event.detail.fullname;
      if (this.isManagedAccount) {
        this.commitAccountData(this.beneficiaries, 'beneficiaries');
      } else {
        this.commitAccountData(
          this.beneficiaries.filter((beneficiary) => {
            if (
              beneficiary.firstName
              || beneficiary.middleName
              || beneficiary.lastName
            ) {
              return true;
            }
            return false;
          }),
          'beneficiaries',
        );

        const beneficiaryNameComponent = document.querySelector(
          `#benefitOf-${index}`,
        );
        if (
          !!this.beneficiaries[index].lastName
          || !!this.beneficiaries[index].firstName
          || !!this.beneficiaries[index].middleName
        ) {
          beneficiaryNameComponent.validationRules.firstName.required = true;
          beneficiaryNameComponent.validationRules.firstName.min = 2;
          beneficiaryNameComponent.validationRules.lastName.required = true;
          beneficiaryNameComponent.validationRules.lastName.min = 2;
        } else {
          beneficiaryNameComponent.validationRules.firstName.required = false;
          beneficiaryNameComponent.validationRules.firstName.min = 0;
          beneficiaryNameComponent.validationRules.lastName.required = false;
          beneficiaryNameComponent.validationRules.lastName.min = 0;
        }
        beneficiaryNameComponent.validateComponent();
      }
    },
    trustAddressChanged(event) {
      const address = {
        line1: event.detail.address.addressLine1,
        line2: event.detail.address.addressLine2,
        city: event.detail.address.cityName,
        state: event.detail.address.stateCode,
        zip: event.detail.address.zipCode,
      };
      this.trustAddress = address;
      this.commitAccountData(this.trustAddress, 'trustAddress');
    },
    clearTrustAddress() {
      this.trustAddress = {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      };
      this.commitAccountData(this.trustAddress, 'trustAddress');
    },
    moreThanOneBene() {
      return this.beneficiaries.length > 1;
    },
    addBeneHelpModal(index) {
      return index === 0;
    },
  },
};
</script>

<style scoped>
.top-margin-zero {
  margin-top: 0;
}
.pvd-form-row .form-row-content .pvd-btn.add-another {
  margin-left: 0;
}
.pvd-form-row .form-row-content .pvd-input.taxpayerID {
  width: 10em;
}
.pvd-btn.remove-bene {
  background-color: transparent;
  border-color: transparent;
  color: #024a7a;
  margin-left: 15em;
}
.ml-1 {
  margin-left: 0.25rem;
}
#trustinfotoggle {
  color: #0e67a9;
  font-weight: bold;
}
</style>
